import { useTranslation } from 'react-i18next';
import { CSSProperties, useState } from 'react';
import { Flex } from '@radix-ui/themes';
import { Avatar, Card, HeaderItalic, Switch, Text } from '@/components/common';
import { PulpLogo } from '@/components/layout';
import { ContinueButton } from '@/components/onboarding';
import { INTENTIONS, MAX_INTENTIONS, type IntentionId } from '@/constants/intentions';
import { cn } from '@/lib/utils';
import { IntentionBadge } from './IntentionBadge';

type IntentionsPickerProps = {
  selected: IntentionId[];
  offThisWeek: boolean;
  onSelectedChange: (selected: IntentionId[]) => void;
  onOffThisWeekChange: (off: boolean) => void;
  onSubmit: () => void;
  loading?: boolean;
};

const WHEEL_SIZE_VW = 85;
const WHEEL_MAX_PX = 360;

const wheelHeight = `${WHEEL_SIZE_VW}vw`;
const wheelWidth = `${WHEEL_SIZE_VW}vw`;
const wheelMax = `${WHEEL_MAX_PX}px`;

// Radius of the satellite constellation, as a percent of the wheel's width/height.
const SATELLITE_RADIUS_PCT = 42;

const toSatellite = (angleDeg: number): CSSProperties => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    top: `${50 - SATELLITE_RADIUS_PCT * Math.cos(rad)}%`,
    left: `${50 + SATELLITE_RADIUS_PCT * Math.sin(rad)}%`,
    transform: 'translate(-50%, -50%)',
  };
};

/** Clockwise from top — matches v90 constellation. */
const SATELLITE_PROPS: Record<IntentionId, CSSProperties> = {
  physical: toSatellite(0),
  drink: toSatellite(60),
  dayout: toSatellite(120),
  online: toSatellite(180),
  takeout: toSatellite(240),
  softnight: toSatellite(300),
};

export function IntentionsPicker({
  selected,
  offThisWeek,
  onSelectedChange,
  onOffThisWeekChange,
  onSubmit,
  loading = false,
}: IntentionsPickerProps) {
  const { t } = useTranslation('onboarding');
  const [shakingId, setShakingId] = useState<IntentionId | null>(null);

  const canSubmit = offThisWeek || selected.length > 0;

  const tapIntention = (id: IntentionId) => {
    if (offThisWeek) return;

    if (selected.includes(id)) {
      onSelectedChange(selected.filter(s => s !== id));
      return;
    }

    if (selected.length >= MAX_INTENTIONS) {
      setShakingId(id);
      window.setTimeout(() => setShakingId(null), 320);
      return;
    }

    onSelectedChange([...selected, id]);
  };

  const setOffThisWeek = (off: boolean) => {
    onOffThisWeekChange(off);
    if (off) onSelectedChange([]);
  };

  const selectedOptions = INTENTIONS.filter(option => selected.includes(option.id));

  return (
    <Flex direction="column" align="center" gap="4" py="2">
      <Flex direction="column" align="center" gap="3" style={{ textAlign: 'center' }}>
        <HeaderItalic>{t('steps.setIntentions.title')}</HeaderItalic>
        <Text as="p" size="1" color="gray" style={{ maxWidth: '320px' }}>
          {t('steps.setIntentions.description')}
        </Text>
      </Flex>

      <Flex
        position="relative"
        my="6"
        mx="auto"
        height={wheelHeight}
        width={wheelWidth}
        maxWidth={wheelMax}
        maxHeight={wheelMax}
        flexShrink="0"
      >
        <Flex
          position="absolute"
          justify="center"
          align="center"
          width="100%"
          height="100%"
          className="pointer-events-none"
        >
          <PulpLogo size="9" />
        </Flex>

        {INTENTIONS.map(option => {
          const isSelected = selected.includes(option.id);
          const disabled = offThisWeek;
          const showBright = isSelected && !disabled;

          return (
            <Flex
              key={option.id}
              asChild
              position="absolute"
              direction="column"
              justify="center"
              align="center"
              gap="1"
            >
              <button
                type="button"
                disabled={disabled}
                onClick={() => tapIntention(option.id)}
                className={cn(
                  'cursor-pointer border-0 transition-transform duration-150 select-none active:scale-[0.94]',
                  disabled && 'pointer-events-none opacity-50'
                )}
                style={{
                  ...SATELLITE_PROPS[option.id],
                  width: '80px',
                }}
              >
                <div
                  className={cn(
                    'flex w-full flex-col items-center gap-1',
                    shakingId === option.id && 'animate-cnst-shake'
                  )}
                >
                  <IntentionBadge option={option} matches={showBright} size="5" />
                  <Text
                    as="div"
                    size="1"
                    wrap="pretty"
                    align="center"
                    weight={isSelected && !disabled ? 'medium' : undefined}
                    className={cn(
                      'transition-[color,font-weight] duration-200',
                      isSelected && !disabled ? 'text-white' : 'text-white/75'
                    )}
                  >
                    {t(option.labelKey)}
                  </Text>
                </div>
              </button>
            </Flex>
          );
        })}
      </Flex>

      {selectedOptions.length > 0 && !offThisWeek && (
        <Flex direction="column" gap="3" width="100%">
          {selectedOptions.map(option => (
            <Card key={option.id} variant="accented">
              <Flex align="center" gap="3" width="100%">
                <Avatar src={option.icon} fallback="" radius="medium" size="4" />
                <Flex direction="column" gap="1" className="min-w-0 flex-1">
                  <HeaderItalic size="4">{t(option.labelKey)}</HeaderItalic>
                  <Text size="1" className="leading-snug text-white/65">
                    {t(option.descKey)}
                  </Text>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Flex>
      )}

      <Flex align="center" justify="between" width="100%" py="3">
        <Text size="2" color="gray">
          {t('steps.setIntentions.offThisWeek')}
        </Text>
        <Switch
          checked={offThisWeek}
          onCheckedChange={checked => setOffThisWeek(checked === true)}
          aria-label={t('steps.setIntentions.offThisWeek')}
        />
      </Flex>

      {offThisWeek && (
        <Text size="1" color="gray">
          {t('steps.setIntentions.takingBreak')}
        </Text>
      )}

      <ContinueButton onClick={onSubmit} disabled={!canSubmit || loading}>
        {offThisWeek ? t('steps.setIntentions.ctaBreak') : t('steps.setIntentions.cta')}
      </ContinueButton>
    </Flex>
  );
}
